import Scan from '../assets/scan.svg'
import Science from '../assets/science.svg'
import Stomach from '../assets/stomach.svg'
import Thoughts from '../assets/thoughts.svg'
import TV from '../assets/tv.svg'
import Wave from '../assets/wave.svg'
import { Footnote } from '../components/curriculum/Footnote'
import { Divider } from '../components/divider/Divider'
import { Explanation } from '../components/explanation/Explanation'
import { Section } from '../components/guides/Section'
import { Title } from '../components/guides/Title'
import { Layout } from '../components/Layout'

const UrgeSurfing = () => {
  return (
    <Layout>
      <div className={'w90 center module'}>
        <h1 className="huge">Urge Surfing</h1>
        <Explanation color="blue">
          <div>
            <strong>Why: </strong>Learn to manage food cravings, which is{' '}
            <strong>really hard</strong> without training{' '}
            <Footnote link="https://pubmed.ncbi.nlm.nih.gov/17544361/" />.
          </div>
        </Explanation>

        <div>
          <h2 className="mt50 mb0">Spot the urge</h2>

          <Section>
            <Title alt="Scan" src={Scan} title={'Look for tension'} />
            <Explanation color="blue">
              <div>
                <strong>Instructions:</strong> Look for tension or tightness,
                especially in areas associated with food, like your mouth.
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title
              alt="Person thinking"
              src={Thoughts}
              title={'Look for unpleasant emotions'}
            />
            <Explanation color="blue">
              <div>
                <strong>Instructions:</strong> Look for the presence of an
                unpleasant emotion, such as boredom or sadness.
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title alt="Stomach" src={Stomach} title={'Look for real hunger'} />
            <Explanation color="blue">
              <div>
                <strong>Instructions:</strong> Look for signs that your hunger
                is <strong>real</strong>. Common signs include a growling
                stomach, a gradual onset, and a feeling than can be satisfied by{' '}
                <strong>any</strong> food, like an apple.
              </div>
            </Explanation>
          </Section>
        </div>

        <Divider className="mb20 mt50" height={4} />

        <div className="mt20">
          <h2 className="mt50 mb0">Manage the urge</h2>

          <Section>
            <Title alt="Wave" src={Wave} title={'Ride the wave'} />
            <Explanation color="blue">
              <div>
                <strong>Instructions:</strong> Imagine the craving as a wave,
                with you gently riding on top of it. Feel the crest and swell of
                the wave. Feel yourself gently bobbing along. Just watch the
                wave rise and fall.
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Insight: </strong>The key to urge surfing is not to{' '}
                <strong>fight</strong> a craving, as that will only prolong it{' '}
                <Footnote link="https://en.wikipedia.org/wiki/Ironic_process_theory" />
                . Instead, you need to <strong>accept</strong> two things. One,
                that the craving cannot make you do anything. Two, you cannot
                fight the craving. Instead, you must <strong>wait</strong> for
                it to go away. This usually occurs within 10 to 20 minutes{' '}
                <Footnote link="https://www.quit.com/quitting/cigarette-cravings.html" />
                . This takes practice, so treat yourself with self-compassion if
                you don{`'`}t get it right away.
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title alt="Test tube" src={Science} title={'Become a scientist'} />
            <Explanation color="blue" className="pb10">
              <div>
                <strong>Instructions:</strong> Inspect the urge with a friendly,
                non-judgemental curiosity, as if you are a scientist. Here are
                just a couple of questions you could ask.
                <ul className="mb0">
                  <li>
                    Where is it <strong>located</strong>?
                  </li>
                  <li>
                    Does it have a <strong>taste</strong>?{' '}
                  </li>
                  <li>
                    Does it have a <strong>smell</strong>?{' '}
                  </li>
                  <li>
                    What does it <strong>feel like</strong>? Do you feel
                    tension, tingles, tightness, or numbness?
                  </li>
                  <li>
                    What does it <strong>look like</strong>? Is it jagged or
                    smooth, hard or soft? Does it have a color?
                  </li>
                </ul>
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip: </strong> The exact questions don{`'`}t matter,
                what matters is cultivating an <strong>attitude</strong> of
                acceptance and curiosity.
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title alt="TV" src={TV} title={'Label and distract'} />
            <Explanation color="blue" className="pb10">
              <div>
                <strong>Instructions:</strong> Label the craving internally with
                a word, like {`"`}craving{`"`} or {`"`}
                desire.{`"`} Then, distract yourself until it fades away. Here
                are a couple of ideas:
                <ul className="mb0">
                  <li>Entertainment: TV, video games, books, podcasts, etc.</li>
                  <li>Conversations with friends and family</li>
                  <li>Exercise</li>
                  <li>Playing with pets</li>
                  <li>Projects: Art, music, programming, etc.</li>
                </ul>
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip:</strong> This technique is the easiest to do,
                especially for beginners. The key is to remember that you are{' '}
                <strong>not</strong> trying to suppress the craving. Instead,
                you are simply trying to shift the bulk of your attention to a
                more pleasant source until the craving fades. Try to remain
                mindful that the craving exists and accept its presence.
              </div>
            </Explanation>
          </Section>
        </div>
      </div>
    </Layout>
  )
}

export default UrgeSurfing
